<template>
  <CatalogoPagina
    titulo="Empleados"
    prefijo="empleados"
    :resumen="resumen"
    :columnas="columnas"
    :total="total"
    :pagina="paginaActual"
    :total-paginas="totalPaginas"
    :limite="limite"
    :cargando="cargando"
    :ya-cargo="yaCargo"
    :error="errorCarga"
    mensaje-error="No pudimos cargar los empleados."
    :orden-campo="ordenCampo"
    :orden-asc="ordenAsc"
    :aviso="aviso"
    @limite="alCambiarLimite"
    @ordenar="ordenarPor"
    @pagina="cambiarPagina"
    @reintentar="initialize"
    @cerrar-aviso="aviso = ''"
  >
    <template #acciones>
      <button type="button" class="rm-btn rm-btn-primario" data-testid="empleados-nuevo" @click="nuevoEmpleado">
        <AppIcon name="plus" size="h-4 w-4" />
        Nuevo empleado
      </button>
    </template>

    <template #filtros>
      <div class="em-pestanas" role="tablist" aria-label="Puesto">
        <button
          v-for="pestana in pestanas"
          :key="pestana.id || 'todos'"
          type="button"
          role="tab"
          class="em-pestana"
          :class="{ activa: filtroPuesto === pestana.id }"
          :aria-selected="filtroPuesto === pestana.id ? 'true' : 'false'"
          :data-testid="'empleados-pestana-' + (pestana.clave)"
          @click="cambiarPuesto(pestana.id)"
        >
          {{ pestana.label }}
          <span class="em-cuenta">{{ pestana.cuenta }}</span>
        </button>
      </div>
      <AppBusqueda
        v-model="busqueda"
        placeholder="Buscar empleado"
        testid="empleados-buscar"
        @buscar="aplicarFiltros"
      />
    </template>

    <template #filas>
      <tr v-for="empleado in empleados" :key="empleado._id" class="em-fila" data-testid="empleado-fila">
        <td class="em-celda-empleado">
          <div class="cat-nombre">
            <span class="cat-avatar" aria-hidden="true">{{ iniciales(empleado) }}</span>
            <div class="cat-nombre-datos">
              <span class="cat-nombre-texto">{{ nombreDe(empleado) }}</span>
              <span v-if="empleado.email" class="cat-meta">{{ empleado.email }}</span>
            </div>
          </div>
        </td>
        <td>
          <div class="cat-pila">
            <span>{{ puestoDe(empleado) }}</span>
            <span v-if="empleado.branch" class="cat-meta">{{ sucursalDe(empleado.branch) }}</span>
          </div>
        </td>
        <td class="em-telefono">
          <template v-if="telefonosDe(empleado.telephones).length">{{ telefonosDe(empleado.telephones)[0] }}</template>
          <span v-else class="cat-sin-dato" aria-label="Sin teléfono">—</span>
        </td>
        <td class="em-celda-numero">
          <template v-if="empleado.servicios">{{ cifra(empleado.servicios) }}<span class="em-unidad"> servicios en 12 meses</span></template>
          <span v-else class="cat-sin-dato" aria-label="Sin servicios">—</span>
        </td>
        <td>
          <span v-if="empleado.usuario" class="em-usuario" :title="'Entra al sistema como ' + empleado.usuario">
            <AppIcon name="lock" size="h-3.5 w-3.5" />{{ empleado.usuario }}
          </span>
          <span v-else class="cat-sin-dato"><span aria-hidden="true">—</span><span class="em-sin">Sin usuario</span></span>
        </td>
        <td class="cat-fila-acciones">
          <button
            type="button"
            class="cat-icono-btn"
            :aria-label="'Editar a ' + nombreDe(empleado)"
            data-testid="empleado-editar"
            @click="editarEmpleado(empleado)"
          >
            <AppIcon name="pencil" size="h-4 w-4" />
          </button>
          <button
            type="button"
            class="cat-icono-btn peligro"
            :aria-label="'Dar de baja a ' + nombreDe(empleado)"
            data-testid="empleado-eliminar"
            @click="pedirEliminar(empleado)"
          >
            <AppIcon name="trash" size="h-4 w-4" />
          </button>
        </td>
      </tr>
    </template>

    <template #vacio>
      <AppIcon :name="busqueda.trim() ? 'search' : 'users'" size="h-8 w-8" />
      <p v-if="busqueda.trim()">No hay empleados que coincidan con “{{ busqueda.trim() }}”.</p>
      <template v-else>
        <p>No hay empleados {{ filtroPuesto ? 'con este puesto' : 'registrados' }}.</p>
        <button type="button" class="rm-btn rm-btn-primario" @click="nuevoEmpleado">Registrar empleado</button>
      </template>
    </template>

    <BaseModal :visible="dialogoForm" :title="tituloForm" max-width="max-w-[860px]" persistent @close="cerrarFormulario">
      <form novalidate class="em-form" data-testid="empleado-form" @submit.prevent="guardarEmpleado">
        <AppField
          v-model="form.name"
          class="em-c2"
          label="Nombre"
          required
          placeholder="Juan"
          :error="mostrarError('name')"
          testid="empleado-nombre"
          @blur="alSalirDeNombre('name')"
        />
        <AppField
          v-model="form.first_lastname"
          class="em-c2"
          label="Apellido paterno"
          required
          placeholder="López"
          :error="mostrarError('first_lastname')"
          testid="empleado-apellido-paterno"
          @blur="alSalirDeNombre('first_lastname')"
        />
        <AppField
          v-model="form.second_lastname"
          class="em-c2"
          label="Apellido materno"
          placeholder="Pérez"
          testid="empleado-apellido-materno"
          @blur="alSalirDeNombre('second_lastname')"
        />
        <AppField v-slot="{ controlId }" class="em-c2" label="Puesto" required :error="mostrarError('position')" testid="empleado-puesto">
          <select
            :id="controlId"
            v-model="form.position"
            :class="['app-field-control em-select', { vacio: !form.position }]"
            data-testid="empleado-puesto"
            @blur="tocar('position')"
          >
            <option value="" disabled>Elige un puesto</option>
            <option v-for="puesto in puestos" :key="puesto._id" :value="puesto._id">{{ etiquetaPuesto(puesto.name) }}</option>
          </select>
        </AppField>
        <AppField v-slot="{ controlId }" class="em-c2" label="Sucursal" required :error="mostrarError('branch')" testid="empleado-sucursal">
          <select
            :id="controlId"
            v-model="form.branch"
            :class="['app-field-control em-select', { vacio: !form.branch }]"
            data-testid="empleado-sucursal"
            @blur="tocar('branch')"
          >
            <option value="" disabled>Elige una sucursal</option>
            <option v-for="sucursal in sucursales" :key="sucursal._id" :value="sucursal._id">{{ sucursalDe(sucursal) }}</option>
          </select>
        </AppField>
        <AppField
          v-model="telefonoVisible"
          class="em-c2"
          label="Celular"
          required
          type="tel"
          inputmode="numeric"
          maxlength="14"
          placeholder="(667) 319 0560"
          :format="mascaraTelefono"
          :error="mostrarError('telefono')"
          testid="empleado-celular"
          @blur="tocar('telefono')"
        />
        <AppField v-slot="{ controlId }" class="em-c3" label="Correo" :error="mostrarError('email')" testid="empleado-correo">
          <AppCorreo
            :id="controlId"
            v-model="form.email"
            :invalid="Boolean(mostrarError('email'))"
            testid="empleado-correo"
            @blur="tocar('email')"
          />
        </AppField>
        <AppField
          v-model="nacimiento"
          class="em-c3"
          label="Nacimiento"
          inputmode="numeric"
          maxlength="10"
          placeholder="DD/MM/AAAA"
          :format="mascaraFecha"
          :error="mostrarError('birthdate')"
          testid="empleado-nacimiento"
          @blur="tocar('birthdate')"
        />
        <AppField
          v-model="form.address"
          class="em-c6"
          label="Dirección"
          placeholder="Calle, número y colonia"
          testid="empleado-direccion"
          @blur="form.address = capitalizarDireccion(form.address)"
        />
      </form>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarFormulario">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-primario"
          :disabled="guardando"
          data-testid="empleado-guardar"
          @click="guardarEmpleado"
        >
          {{ guardando ? 'Guardando…' : 'Guardar' }}
        </button>
      </template>
    </BaseModal>

    <BaseModal :visible="dialogoEliminar" title="Dar de baja empleado" @close="cerrarEliminar">
      <p class="cat-confirmacion">
        ¿Dar de baja a <b>{{ empleadoSeleccionado ? nombreDe(empleadoSeleccionado) : '' }}</b>? Ya no aparecerá para
        asignarlo en órdenes.
        <template v-if="empleadoSeleccionado && empleadoSeleccionado.usuario">
          Su usuario <b>{{ empleadoSeleccionado.usuario }}</b> sigue activo: dalo de baja en Usuarios si ya no trabaja aquí.
        </template>
      </p>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarEliminar">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-peligro"
          :disabled="guardando"
          data-testid="empleado-confirmar-eliminar"
          @click="confirmarEliminar"
        >
          {{ guardando ? 'Dando de baja…' : 'Dar de baja' }}
        </button>
      </template>
    </BaseModal>
  </CatalogoPagina>
</template>

<script>
/*
 * Empleados — catálogo migrado sobre CatalogoPagina.
 *
 * Paginación, búsqueda (nombre, correo o teléfono), filtro por puesto y orden
 * en el backend (/api/employee/paginated). Lo más usado primero: servicios
 * hechos como mecánico en los últimos 12 meses. Se ve qué empleado tiene
 * usuario del sistema. Nombres capitalizados y sin relleno ("." como
 * apellido no se muestra), celular (667) 319 0560 y nacimiento DD/MM/AAAA.
 * Dar de baja es lógico (status 0).
 */
import ApiService from '@/services/apis'
import AppBusqueda from '@/components/ui/AppBusqueda.vue'
import AppCorreo from '@/components/ui/AppCorreo.vue'
import AppField from '@/components/ui/AppField.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import CatalogoPagina, { LIMITE_BASE } from '@/components/catalogo/CatalogoPagina.vue'
import { validateNoErrors } from '@/helpers/helpers'
import {
  capitalizarDireccion,
  capitalizarNombre,
  fechaTextoAISO,
  isoAFechaTexto,
  mascaraFecha,
  mascaraTelefono,
  nombreDePersona,
  telefonosDe,
} from '@/utils/formato'
import { paginaEquivalente } from '@/utils/paginacion'

const formatoCifra = new Intl.NumberFormat('es-MX')

// Los puestos se guardan en mayúsculas y sin acentos
const PUESTOS = {
  MECANICO: { singular: 'Mecánico', plural: 'Mecánicos' },
  VENTAS: { singular: 'Ventas', plural: 'Ventas' },
}

export function etiquetaPuesto(nombre, { plural = false } = {}) {
  const clave = String(nombre || '').trim().toUpperCase()
  if (PUESTOS[clave]) return plural ? PUESTOS[clave].plural : PUESTOS[clave].singular
  return capitalizarNombre(clave)
}

function formularioVacio() {
  return {
    name: '',
    first_lastname: '',
    second_lastname: '',
    telefono: '',
    email: '',
    birthdate: '',
    address: '',
    position: '',
    branch: '',
  }
}

/* Igual que moment(fecha).utc().format('YYYY-MM-DD') de la pantalla vieja */
function fechaISO(fecha) {
  if (!fecha) return ''
  const d = new Date(fecha)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}

function idDe(valor) {
  return valor && typeof valor === 'object' ? valor._id || '' : valor || ''
}

export default {
  name: 'empleados',
  components: { AppBusqueda, AppCorreo, AppField, AppIcon, BaseModal, CatalogoPagina },
  data() {
    return {
      empleados: [],
      puestos: [],
      sucursales: [],
      totales: { total: 0, porPuesto: {} },
      filtroPuesto: '',
      busqueda: '',
      ordenCampo: 'servicios',
      ordenAsc: false,
      paginaActual: 1,
      total: 0,
      totalPaginas: 1,
      limite: LIMITE_BASE,
      cargando: true,
      yaCargo: false,
      errorCarga: false,
      guardando: false,
      dialogoForm: false,
      dialogoEliminar: false,
      empleadoSeleccionado: null,
      form: formularioVacio(),
      nacimientoTexto: '',
      tocados: {},
      intentoGuardar: false,
      aviso: '',
      sesion: JSON.parse(sessionStorage.getItem('sesion') || 'null'),
    }
  },
  computed: {
    pestanas() {
      return [
        { id: '', clave: 'todos', label: 'Todos', cuenta: this.totales.total },
        ...this.puestos.map((p) => ({
          id: p._id,
          clave: String(p.name || '').toLowerCase(),
          label: etiquetaPuesto(p.name, { plural: true }),
          cuenta: this.totales.porPuesto[p._id] || 0,
        })),
      ]
    },
    columnas() {
      return [
        { value: 'nombre', label: 'Empleado' },
        { value: 'puesto', label: 'Puesto', ancho: '170px' },
        { value: 'telefono', label: 'Celular', ancho: '150px', ordenable: false },
        { value: 'servicios', label: 'Servicios (12 meses)', ancho: '170px', alinear: 'derecha' },
        { value: 'usuario', label: 'Usuario', ancho: '140px', ordenable: false },
      ]
    },
    resumen() {
      if (this.busqueda.trim()) return `${this.cifra(this.total)} ${this.total === 1 ? 'coincidencia' : 'coincidencias'}`
      return `${this.cifra(this.totales.total)} empleados activos`
    },
    tituloForm() {
      return this.empleadoSeleccionado ? 'Editar empleado' : 'Nuevo empleado'
    },
    telefonoVisible: {
      get() {
        return mascaraTelefono(this.form.telefono)
      },
      set(valor) {
        this.form.telefono = String(valor).replace(/\D/g, '')
      },
    },
    nacimiento: {
      get() {
        return this.nacimientoTexto
      },
      set(valor) {
        this.nacimientoTexto = valor
        this.form.birthdate = fechaTextoAISO(valor) || ''
      },
    },
    hoy() {
      const d = new Date()
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },
    errores() {
      const f = this.form
      const e = {}
      if (!f.name.trim()) e.name = 'Requerido.'
      if (!f.first_lastname.trim()) e.first_lastname = 'Requerido.'
      if (!f.position) e.position = 'Requerido.'
      if (!f.branch) e.branch = 'Requerido.'
      if (!f.telefono) e.telefono = 'Requerido.'
      else if (!validateNoErrors('telephones', f.telefono)) e.telefono = 'Deben ser 10 dígitos.'
      if (f.email && !validateNoErrors('email', f.email)) e.email = 'Formato no válido.'
      if (this.nacimientoTexto) {
        const iso = fechaTextoAISO(this.nacimientoTexto)
        if (!iso) e.birthdate = 'Usa el formato DD/MM/AAAA.'
        else if (iso < '1930-01-01' || iso > this.hoy) e.birthdate = 'Fecha fuera de rango.'
      }
      return e
    },
  },
  created() {
    this.ultimaConsulta = 0
    this.limiteListo = false
    this.cargarCatalogos()
  },
  beforeDestroy() {
    clearTimeout(this.avisoTimer)
  },
  methods: {
    capitalizarDireccion,
    mascaraFecha,
    mascaraTelefono,
    telefonosDe,
    etiquetaPuesto,
    cifra(n) {
      return formatoCifra.format(n)
    },
    nombreDe(empleado) {
      return nombreDePersona(empleado) || 'Sin nombre'
    },
    iniciales(empleado) {
      const partes = this.nombreDe(empleado).split(' ')
      return ((partes[0] || '')[0] || '') + ((partes[1] || '')[0] || '')
    },
    puestoDe(empleado) {
      return empleado.position ? etiquetaPuesto(empleado.position.name) : 'Sin puesto'
    },
    sucursalDe(sucursal) {
      return sucursal ? capitalizarNombre(sucursal.name) : ''
    },
    async cargarCatalogos() {
      try {
        const [puestos, sucursales] = await Promise.all([ApiService.puestos(), ApiService.sucursal()])
        this.puestos = (puestos && puestos.data) || []
        this.sucursales = (sucursales && sucursales.data) || []
      } catch (err) {
        // Sin catálogos el filtro muestra solo "Todos"; el formulario avisa al guardar
      }
    },
    async initialize() {
      const consulta = ++this.ultimaConsulta
      this.cargando = true
      this.errorCarga = false
      try {
        const res = await ApiService.empleadoPaginado({
          pagina: this.paginaActual,
          limite: this.limite,
          busqueda: this.busqueda.trim(),
          puesto: this.filtroPuesto,
          ordenarPor: this.ordenCampo,
          direccion: this.ordenAsc ? 'asc' : 'desc',
        })
        if (consulta !== this.ultimaConsulta) return
        if (!Array.isArray(res?.empleados)) throw new Error('Respuesta inválida del servicio')
        this.empleados = res.empleados
        this.total = Number(res.total) || 0
        this.totalPaginas = Math.max(1, Number(res.totalPages) || 1)
        if (res.totales) this.totales = { total: res.totales.total || 0, porPuesto: res.totales.porPuesto || {} }
        this.yaCargo = true
        if (this.paginaActual > this.totalPaginas) {
          this.paginaActual = this.totalPaginas
          return this.initialize()
        }
      } catch (err) {
        if (consulta !== this.ultimaConsulta) return
        this.errorCarga = true
        this.yaCargo = false
        this.empleados = []
        this.total = 0
      } finally {
        if (consulta === this.ultimaConsulta) this.cargando = false
      }
    },
    alCambiarLimite(nuevo) {
      if (!this.limiteListo) {
        this.limiteListo = true
        this.limite = nuevo
        return this.initialize()
      }
      if (nuevo === this.limite) return
      this.paginaActual = paginaEquivalente(this.paginaActual, this.limite, nuevo)
      this.limite = nuevo
      this.initialize()
    },
    cambiarPuesto(id) {
      if (id === this.filtroPuesto) return
      this.filtroPuesto = id
      this.aplicarFiltros()
    },
    ordenarPor(campo) {
      if (campo === this.ordenCampo) {
        this.ordenAsc = !this.ordenAsc
      } else {
        this.ordenCampo = campo
        // Nombre y puesto empiezan de la A; los servicios, del que más tiene
        this.ordenAsc = campo !== 'servicios'
      }
      this.aplicarFiltros()
    },
    aplicarFiltros() {
      this.paginaActual = 1
      this.initialize()
    },
    cambiarPagina(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas || pagina === this.paginaActual) return
      this.paginaActual = pagina
      this.initialize()
    },
    abrirFormulario(empleado) {
      this.empleadoSeleccionado = empleado
      if (empleado) {
        const telefono = telefonosDe(empleado.telephones)[0] || ''
        this.form = {
          name: capitalizarNombre(String(empleado.name || '').trim()),
          first_lastname: capitalizarNombre(String(empleado.first_lastname || '').trim()),
          second_lastname: capitalizarNombre(String(empleado.second_lastname || '').trim()),
          telefono: telefono.replace(/\D/g, ''),
          email: empleado.email || '',
          birthdate: fechaISO(empleado.birthdate),
          address: capitalizarDireccion(empleado.address || ''),
          position: idDe(empleado.position),
          branch: idDe(empleado.branch),
        }
      } else {
        // La sucursal de quien captura, o la única que haya
        const propia = idDe(this.sesion && this.sesion.employee && this.sesion.employee.branch)
        this.form = { ...formularioVacio(), branch: propia || (this.sucursales.length === 1 ? this.sucursales[0]._id : '') }
      }
      this.nacimientoTexto = isoAFechaTexto(this.form.birthdate)
      this.tocados = {}
      this.intentoGuardar = false
      this.dialogoForm = true
    },
    nuevoEmpleado() {
      this.abrirFormulario(null)
    },
    editarEmpleado(empleado) {
      this.abrirFormulario(empleado)
    },
    cerrarFormulario() {
      this.dialogoForm = false
      this.empleadoSeleccionado = null
    },
    tocar(campo) {
      this.tocados = { ...this.tocados, [campo]: true }
    },
    mostrarError(campo) {
      return this.tocados[campo] || this.intentoGuardar ? this.errores[campo] || '' : ''
    },
    alSalirDeNombre(campo) {
      this.form[campo] = capitalizarNombre(this.form[campo])
      this.tocar(campo)
    },
    async guardarEmpleado() {
      this.intentoGuardar = true
      if (Object.keys(this.errores).length || this.guardando) return
      const f = this.form
      const payload = {
        name: capitalizarNombre(f.name),
        first_lastname: capitalizarNombre(f.first_lastname),
        second_lastname: capitalizarNombre(f.second_lastname),
        telephones: [f.telefono],
        email: f.email.trim(),
        birthdate: f.birthdate || null,
        address: capitalizarDireccion(f.address),
        position: f.position,
        branch: f.branch,
      }
      const editando = Boolean(this.empleadoSeleccionado)
      this.guardando = true
      try {
        if (editando) await ApiService.empleadoEditar(this.empleadoSeleccionado._id, payload)
        else await ApiService.empleadoNuevo(payload)
        this.cerrarFormulario()
        await this.initialize()
        this.mostrarAviso(editando ? 'Empleado actualizado' : 'Empleado agregado')
      } catch (err) {
        // El servicio ya muestra el motivo; el formulario sigue abierto
      } finally {
        this.guardando = false
      }
    },
    pedirEliminar(empleado) {
      this.empleadoSeleccionado = empleado
      this.dialogoEliminar = true
    },
    cerrarEliminar() {
      this.dialogoEliminar = false
      this.empleadoSeleccionado = null
    },
    async confirmarEliminar() {
      this.guardando = true
      try {
        await ApiService.empleadoEditar(this.empleadoSeleccionado._id, { status: 0 })
        const nombre = this.nombreDe(this.empleadoSeleccionado)
        this.cerrarEliminar()
        if (this.empleados.length === 1 && this.paginaActual > 1) this.paginaActual--
        await this.initialize()
        this.mostrarAviso(`${nombre} dado de baja`)
      } catch (err) {
        // El servicio ya muestra el motivo
      } finally {
        this.guardando = false
      }
    },
    mostrarAviso(mensaje) {
      this.aviso = mensaje
      clearTimeout(this.avisoTimer)
      this.avisoTimer = setTimeout(() => {
        this.aviso = ''
      }, 4500)
    },
  },
}
</script>

<style scoped>
.em-pestanas {
  display: inline-flex;
  gap: 2px;
  padding: 4px;
  border: 1px solid #e2e8ef;
  border-radius: 13px;
  background: #f8fafc;
}
.em-pestana {
  display: inline-flex;
  min-height: 34px;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #526273;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;
}
.em-pestana:hover:not(.activa) {
  background: #edf3f8;
  color: #0b1b2e;
}
.em-pestana.activa {
  background: #12304f;
  color: #fff;
  box-shadow: 0 2px 5px rgba(16, 42, 67, 0.17);
}
.em-pestana:focus-visible {
  outline: 3px solid #7ab9f2;
  outline-offset: 2px;
}
.em-cuenta {
  min-width: 22px;
  padding: 1px 6px;
  border-radius: 999px;
  background: rgba(82, 98, 115, 0.12);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  text-align: center;
}
.em-pestana.activa .em-cuenta {
  background: rgba(255, 255, 255, 0.18);
}
.em-form {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px 14px;
}
.em-c2 {
  grid-column: span 2;
}
.em-c3 {
  grid-column: span 3;
}
.em-c6 {
  grid-column: 1 / -1;
}
.em-select {
  appearance: none;
  padding-right: 36px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%233d4b5f' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M19.5 8.25l-7.5 7.5-7.5-7.5'/%3E%3C/svg%3E");
  background-position: right 12px center;
  background-repeat: no-repeat;
  background-size: 16px;
  cursor: pointer;
}
/* Sin elegir: el texto de ayuda se ve como ayuda, no como valor */
.em-select.vacio {
  color: #8a97a8;
}
.em-select option {
  color: #0b1b2e;
}
.em-telefono {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.cat-pagina .cat-tabla td.em-celda-numero {
  color: #0b1b2e;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.em-usuario {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #edf4fc;
  color: #0a4f9c;
  font-size: 12px;
  font-weight: 700;
}
/* "Sin usuario" se lee en voz alta y en móvil; en escritorio basta el guion */
.em-sin,
.em-unidad {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
@media (max-width: 859px) {
  .em-form {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .em-c2,
  .em-c3 {
    grid-column: span 1;
  }
}
@media (max-width: 700px) {
  .em-form {
    grid-template-columns: minmax(0, 1fr);
  }
  .em-c2,
  .em-c3 {
    grid-column: 1 / -1;
  }
  .cat-pagina .cat-tabla td.em-celda-numero {
    font-size: 12px;
    font-weight: 500;
    text-align: left;
  }
  .em-sin,
  .em-unidad {
    position: static;
    width: auto;
    height: auto;
    clip: auto;
  }
}
</style>
